import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, TrashSimple, MagnifyingGlass, Copy, FunnelSimple, FileText, DownloadSimple, PencilSimple, Link as LinkIcon, Printer, Sparkle } from "@phosphor-icons/react";
import { printFilterSummary } from "@/lib/printSummary";
import { MultiFilePicker } from "@/components/MultiFilePicker";
import { extractFromFile } from "@/lib/invoiceExtractor";

function fmtDate(s){ if(!s) return "—"; if(/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; const d=new Date(s); if(Number.isNaN(d.getTime())) return s; return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});}
function fmtRM(n){ const v = Number(n || 0); return `RM ${v.toLocaleString("en-MY",{minimumFractionDigits:2,maximumFractionDigits:2})}`;}

export default function TCAndTCSR() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const [q, setQ] = useState(params.get("q") || "");
  const [source, setSource] = useState(params.get("source") || "");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [sortKey, setSortKey] = useState(params.get("sort") || "date");
  const [sortDir, setSortDir] = useState(params.get("dir") === "asc" ? "asc" : "desc");

  useEffect(() => {
    const n = {};
    if (q) n.q = q; if (source) n.source = source; if (from) n.from = from; if (to) n.to = to;
    if (sortKey) { n.sort = sortKey; n.dir = sortDir; }
    setParams(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, source, from, to, sortKey, sortDir]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/tc-records"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.filename}"?`)) return;
    try { await api.delete(`/tc-records/${r.id}`); setItems(xs => xs.filter(x => x.id !== r.id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openFile = (id) => {
    const t = localStorage.getItem("access_token") || "";
    window.open(`${API}/tc-records/${id}/download?auth=${encodeURIComponent(t)}`, "_blank");
  };
  const shareLink = async () => {
    const url = window.location.href;
    try { await navigator.clipboard.writeText(url); toast.success("Filtered link copied"); return; } catch {}
    try {
      const ta = document.createElement("textarea"); ta.value = url; ta.style.position="fixed"; ta.style.opacity="0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      toast.success("Filtered link copied");
    } catch { toast.error("Could not copy — copy the URL manually"); }
  };
  const clearF = () => { setQ(""); setSource(""); setFrom(""); setTo(""); setSortKey("date"); setSortDir("desc"); };
  const toggleSort = (k) => {
    if (sortKey !== k) { setSortKey(k); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return items.filter(x => {
      if (n) { const hay = `${x.filename||""} ${x.description||""} ${x.source||""} ${x.inv_no||""}`.toLowerCase(); if (!hay.includes(n)) return false; }
      if (source && x.source !== source) return false;
      if (from && (x.date||"") && x.date < from) return false;
      if (to   && (x.date||"") && x.date > to)   return false;
      return true;
    });
  }, [items, q, source, from, to]);

  const displayed = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    const g = (x) => ({
      date: x.date||"",
      filename: (x.filename||"").toLowerCase(),
      source: x.source||"",
      description: (x.description||"").toLowerCase(),
      inv: (x.inv_no||"").toLowerCase(),
      amount: Number(x.amount||0),
    }[sortKey] ?? "");
    return [...filtered].sort((a,b) => { const va=g(a), vb=g(b); if (va<vb) return -dir; if (va>vb) return dir; return 0; });
  }, [filtered, sortKey, sortDir]);

  const totalAmount = useMemo(() => displayed.reduce((s,x)=>s+Number(x.amount||0),0), [displayed]);
  const hasF = q || source || from || to;

  const onPrint = () => {
    printFilterSummary({
      title: "TC & TCSR — Filter Summary",
      filters: [
        q && `Search: "${q}"`,
        source && `Source: ${source}`,
        from && `From: ${from}`,
        to && `To: ${to}`,
      ].filter(Boolean),
      columns: [
        { key: "date",        label: "Date",        format: fmtDate },
        { key: "filename",    label: "File Name" },
        { key: "inv_no",      label: "Inv No." },
        { key: "description", label: "Description" },
        { key: "source",      label: "Source" },
        { key: "amount",      label: "Amount (RM)", align: "right", format: v => Number(v||0).toFixed(2) },
      ],
      rows: displayed,
      total: totalAmount,
      totalLabel: "Total Amount",
    });
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Accounts</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">TC & TCSR</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Terminal charge slips register. Every <b>Terminal Charges</b> document uploaded on a shipment is auto-mirrored here (source: <span className="font-mono text-indigo-700">shipment</span>). You can also upload standalone TC/TCSR files.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onPrint} data-testid="tc-print-btn" className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150">
            <Printer size={14}/> Print filter summary
          </button>
          <button onClick={shareLink} data-testid="tc-share-btn" className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150">
            <Copy size={14}/> Copy share link
          </button>
          <button onClick={() => setShowNew(true)} data-testid="new-tc-btn" className="bg-[#002FA7] text-white text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-[#00227A] transition-colors duration-150">
            <Plus size={16}/> New TC
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="tc-filters">
        <div className="md:col-span-2 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search filename, inv no, description…" data-testid="tc-search" className="w-full text-sm border border-zinc-200 pl-9 pr-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
        </div>
        <select value={source} onChange={e=>setSource(e.target.value)} data-testid="tc-source-filter" className="text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7] bg-white">
          <option value="">All sources</option>
          <option value="shipment">From Shipment</option>
          <option value="manual">Manual upload</option>
        </select>
        <input type="date" value={from} onChange={e=>setFrom(e.target.value)} data-testid="tc-from" className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
        <input type="date" value={to} onChange={e=>setTo(e.target.value)} data-testid="tc-to" className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
      </div>

      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 bg-white border border-zinc-200 px-3 py-1.5 text-sm" data-testid="tc-total-pill">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Filter total</span>
          <span className="font-display font-black text-zinc-950">{fmtRM(totalAmount)}</span>
          <span className="text-xs text-zinc-500 font-mono">· {displayed.length} item{displayed.length===1?"":"s"}</span>
        </div>
        {hasF && (
          <div className="text-xs text-zinc-500 font-mono flex items-center gap-3">
            <FunnelSimple size={12}/> Filtered from {items.length}
            <button onClick={clearF} className="text-[#002FA7] hover:underline">Clear filters</button>
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500 bg-zinc-50">
              <SortTh label="Date"        k="date"        sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("date")}/>
              <SortTh label="File Name"   k="filename"    sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("filename")}/>
              <SortTh label="Inv No."     k="inv"         sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("inv")}/>
              <SortTh label="Description" k="description" sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("description")}/>
              <SortTh label="Source"      k="source"      sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("source")}/>
              <SortTh label="Amount"      k="amount"      align="right" sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("amount")}/>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-16 text-zinc-500 font-mono text-sm">Loading…</td></tr>
            : displayed.length === 0 ? <tr><td colSpan={7} className="text-center py-16 text-zinc-500"><FileText size={40} weight="duotone" className="mx-auto text-zinc-300 mb-3"/><div className="font-mono text-sm">{items.length===0?"No TC records yet — upload the first one.":"No records match these filters."}</div></td></tr>
            : displayed.map(r => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150" data-testid={`tc-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-zinc-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-4 py-3">
                  <button onClick={()=>openFile(r.id)} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline text-sm" data-testid={`tc-view-${r.id}`}>
                    <FileText size={14} weight="duotone"/> <span className="truncate max-w-[240px]">{r.filename}</span>
                  </button>
                </td>
                <td className="px-4 py-3 font-mono text-zinc-800">{r.inv_no || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3 text-zinc-800 truncate max-w-[280px]" title={r.description}>{r.description || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3">
                  {r.source === "shipment"
                    ? <span className="inline-flex items-center gap-1 text-[11px] font-mono border border-indigo-200 bg-indigo-50 text-indigo-800 px-2 py-0.5 uppercase"><LinkIcon size={11}/> shipment</span>
                    : <span className="inline-flex items-center gap-1 text-[11px] font-mono border border-zinc-200 bg-zinc-50 text-zinc-700 px-2 py-0.5 uppercase">manual</span>}
                </td>
                <td className="px-4 py-3 text-right font-mono text-zinc-900 whitespace-nowrap">{fmtRM(r.amount)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={()=>openFile(r.id)} data-testid={`tc-download-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150"><DownloadSimple size={14}/> File</button>
                  <button onClick={()=>setEditing(r)} data-testid={`tc-edit-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150 ml-1"><PencilSimple size={14}/> Edit</button>
                  {(user?.role === "admin" || r.created_by_id === user?.id) && (
                    <button onClick={()=>del(r)} data-testid={`tc-delete-${r.id}`} className="inline-flex items-center gap-1 text-xs text-red-700 px-2 py-1 hover:bg-red-50 transition-colors duration-150 ml-1"><TrashSimple size={14}/> Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {displayed.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-zinc-300 bg-zinc-50">
                <td colSpan={5} className="px-4 py-3 text-right text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-600">Total</td>
                <td className="px-4 py-3 text-right font-mono font-black text-zinc-950" data-testid="tc-footer-total">{fmtRM(totalAmount)}</td>
                <td/>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showNew && <NewTCModal onClose={()=>setShowNew(false)} onCreated={rs=>{ setItems(xs=>[...rs,...xs]); setShowNew(false); }}/>}
      {editing && <EditTCModal record={editing} onClose={()=>setEditing(null)} onSaved={u=>{ setItems(xs=>xs.map(x=>x.id===u.id?{...x,...u}:x)); setEditing(null); }}/>}
    </Layout>
  );
}

function SortTh({ label, k, sortKey, dir, onClick, align }) {
  const active = sortKey === k;
  const arrow = !active ? "↕" : dir === "asc" ? "↑" : "↓";
  return (
    <th className={`${align==="right"?"text-right":"text-left"} px-4 py-3 select-none`}>
      <button type="button" onClick={onClick} data-testid={`sort-${k}`} className={`inline-flex items-center gap-1 uppercase tracking-[0.05em] hover:text-zinc-900 transition-colors duration-150 ${active?"text-zinc-900":"text-zinc-500"}`}>
        {label}<span className={`font-mono text-[11px] ${active?"text-[#002FA7]":"text-zinc-400"}`}>{arrow}</span>
      </button>
    </th>
  );
}

function NewTCModal({ onClose, onCreated }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [invNo, setInvNo] = useState("");
  const [amount, setAmount] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [invSuggestion, setInvSuggestion] = useState("");
  const [amtSuggestion, setAmtSuggestion] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannedNames = useRef(new Set());

  useEffect(() => {
    const f = files[files.length - 1];
    if (!f || scannedNames.current.has(f.name)) return;
    scannedNames.current.add(f.name);
    (async () => {
      setScanning(true);
      const { invoiceNumber, amount: amt } = await extractFromFile(f);
      setScanning(false);
      if (invoiceNumber) {
        setInvSuggestion(invoiceNumber);
        setInvNo(cur => cur.trim() ? cur : invoiceNumber);
      }
      if (amt != null) {
        setAmtSuggestion(amt);
        setAmount(cur => (cur && Number(cur) > 0) ? cur : String(amt));
      }
    })();
  }, [files]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!files.length) { toast.error("Please attach at least one file"); return; }
    setSaving(true);
    try {
      const created = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const fd = new FormData();
        fd.append("date", date);
        // Custom display name only makes sense when uploading a single file
        fd.append("filename_display", files.length === 1 ? displayName.trim() : "");
        fd.append("description", description.trim());
        fd.append("inv_no", invNo.trim());
        fd.append("amount", String(amount || "0"));
        fd.append("file", f);
        const { data } = await api.post("/tc-records", fd, { headers: { "Content-Type": "multipart/form-data" }});
        created.push(data);
      }
      toast.success(created.length === 1 ? "TC uploaded" : `${created.length} TC records uploaded`);
      onCreated(created);
    }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return <Modal title="New TC / TCSR" testid="new-tc-modal" onClose={onClose}>
    <form onSubmit={submit} className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Date" required><input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid="tc-date-input" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
        <Fld label="Invoice #">
          <input value={invNo} onChange={e=>setInvNo(e.target.value)} data-testid="tc-inv-input" placeholder="e.g. TC-2026-0012" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/>
          <InvSuggestion scanning={scanning} suggestion={invSuggestion} invNo={invNo} onUse={()=>{setInvNo(invSuggestion); setInvSuggestion("");}} onDismiss={()=>setInvSuggestion("")}/>
        </Fld>
      </div>
      <Fld label="Amount (RM)">
        <input type="number" step="0.01" min="0" inputMode="decimal" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="tc-amount-input" placeholder="0.00" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/>
        <AmountSuggestion scanning={scanning} suggestion={amtSuggestion} current={amount} onUse={()=>{setAmount(String(amtSuggestion)); setAmtSuggestion(null);}} onDismiss={()=>setAmtSuggestion(null)}/>
      </Fld>
      {files.length <= 1 && (
        <Fld label="Display file name (optional, single-file only)"><input value={displayName} onChange={e=>setDisplayName(e.target.value)} placeholder="Overrides the uploaded filename" data-testid="tc-name-input" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      )}
      <Fld label="Description"><textarea rows={2} value={description} onChange={e=>setDescription(e.target.value)} data-testid="tc-desc-input" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Files (any type — add multiple)" required>
        <MultiFilePicker files={files} onChange={setFiles} testid="tc-file" required hint="e.g. PDF, image, Excel, ZIP"/>
      </Fld>
      <ModalFooter onClose={onClose} saving={saving} submitTestId="tc-submit-btn"/>
    </form>
  </Modal>;
}

function EditTCModal({ record, onClose, onSaved }) {
  const [date, setDate] = useState(record.date || "");
  const [filename, setFilename] = useState(record.filename || "");
  const [description, setDescription] = useState(record.description || "");
  const [invNo, setInvNo] = useState(record.inv_no || "");
  const [amount, setAmount] = useState(record.amount != null ? String(record.amount) : "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/tc-records/${record.id}`, {
        date, filename: filename.trim(), description: description.trim(),
        inv_no: invNo.trim(), amount: Number(amount || 0),
      });
      toast.success("Saved"); onSaved(data);
    }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return <Modal title="Edit TC record" testid="edit-tc-modal" onClose={onClose}>
    <form onSubmit={submit} className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Date"><input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid="tc-edit-date" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
        <Fld label="Invoice #"><input value={invNo} onChange={e=>setInvNo(e.target.value)} data-testid="tc-edit-inv" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
      </div>
      <Fld label="Amount (RM)"><input type="number" step="0.01" min="0" inputMode="decimal" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="tc-edit-amount" placeholder="0.00" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="File name"><input value={filename} onChange={e=>setFilename(e.target.value)} data-testid="tc-edit-name" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Description"><textarea rows={2} value={description} onChange={e=>setDescription(e.target.value)} data-testid="tc-edit-desc" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <ModalFooter onClose={onClose} saving={saving} submitTestId="tc-edit-save"/>
    </form>
  </Modal>;
}

export function Modal({ title, testid, onClose, children }) {
  return <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
    <div className="bg-white border border-zinc-200 shadow-2xl w-full max-w-lg">
      <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
        <div className="font-display font-black text-lg tracking-tight text-zinc-950">{title}</div>
        <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900 p-1"><X size={16}/></button>
      </div>
      {children}
    </div>
  </div>;
}
export function ModalFooter({ onClose, saving, submitTestId }) {
  return <div className="flex items-center justify-end gap-2 border-t border-zinc-200 -mx-5 -mb-5 mt-2 px-5 py-3 bg-zinc-50">
    <button type="button" onClick={onClose} className="text-sm border border-zinc-200 bg-white px-3 py-2 hover:bg-zinc-100">Cancel</button>
    <button type="submit" disabled={saving} data-testid={submitTestId} className="text-sm bg-[#002FA7] text-white px-4 py-2 hover:bg-[#00227A] disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
  </div>;
}
export function Fld({ label, required, children }) {
  return <label className="block">
    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">{label}{required && <span className="text-[#FF4F00]"> *</span>}</div>
    {children}
  </label>;
}

// Suggestion chip shown below an Inv No input after we scanned a picked file.
export function InvSuggestion({ scanning, suggestion, invNo, onUse, onDismiss }) {
  if (scanning) return <div className="text-[11px] text-zinc-500 font-mono mt-1" data-testid="inv-scanning">Scanning file…</div>;
  if (!suggestion || suggestion === (invNo || "").trim()) return null;
  return (
    <div className="mt-1 flex items-center gap-2 text-[11px] bg-amber-50 border border-amber-200 px-2 py-1 font-mono" data-testid="inv-suggestion">
      <Sparkle size={11} weight="fill" className="text-amber-700"/>
      <span className="text-amber-900">Detected <b>{suggestion}</b> in file</span>
      <button type="button" onClick={onUse} data-testid="inv-suggestion-use" className="text-[#002FA7] hover:underline ml-auto">Use</button>
      <button type="button" onClick={onDismiss} data-testid="inv-suggestion-dismiss" className="text-zinc-500 hover:underline">Dismiss</button>
    </div>
  );
}

// Amount suggestion chip. `suggestion` is a Number, `current` is the string value in the input.
export function AmountSuggestion({ scanning, suggestion, current, onUse, onDismiss }) {
  if (scanning) return null; // InvSuggestion already shows the scanning hint
  if (suggestion == null) return null;
  const cur = Number(current || 0);
  if (cur && Math.abs(cur - suggestion) < 0.01) return null;
  const label = `RM ${Number(suggestion).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="mt-1 flex items-center gap-2 text-[11px] bg-amber-50 border border-amber-200 px-2 py-1 font-mono" data-testid="amt-suggestion">
      <Sparkle size={11} weight="fill" className="text-amber-700"/>
      <span className="text-amber-900">Detected total <b>{label}</b> in file</span>
      <button type="button" onClick={onUse} data-testid="amt-suggestion-use" className="text-[#002FA7] hover:underline ml-auto">Use</button>
      <button type="button" onClick={onDismiss} data-testid="amt-suggestion-dismiss" className="text-zinc-500 hover:underline">Dismiss</button>
    </div>
  );
}
